import React from 'react';

import colors from '../../../constants/colors';
import GLYPHS from './glyphs';

export const FACET_TONES = {
  sessions: colors.main.blue,
  weekStreaks: colors.main.royal,
  milestones: '#5CB96A',
  rare: colors.main.ruby,
};

const LOCKED_TONE = colors.main.placeholdersAndDisabledBtnsText;
const MEDALLION_FILL = colors.main.white;
const RING_TRACK = colors.main.dividers;
const SECONDARY_TONE_OPACITY = 0.6;
const LOCKED_FACET_STROKE_WIDTH = 6;
const CHECK_STROKE_WIDTH = 15;
const MIN_TIER_RING = 0.03;

const hexToRgba = (hex, alpha) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(
    hex.slice(5, 7),
    16,
  )}, ${alpha})`;

const clampProgress = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return Math.min(Math.max(parsed, 0), 1);
};

const renderGlyphPart = (part, index, { filled, tone }) => {
  const shapeProps = part.circle
    ? { cx: part.circle.cx, cy: part.circle.cy, r: part.circle.r }
    : { d: part.d };
  const Shape = part.circle ? 'circle' : 'path';

  const paintProps = part.stroke
    ? {
        fill: 'none',
        stroke: tone,
        strokeWidth: CHECK_STROKE_WIDTH,
        strokeLinecap: 'square',
      }
    : filled
      ? {
          fill: tone,
          fillOpacity: part.tone === 2 ? SECONDARY_TONE_OPACITY : 1,
          ...(part.evenodd ? { fillRule: 'evenodd' } : {}),
        }
      : {
          fill: 'none',
          stroke: tone,
          strokeWidth: LOCKED_FACET_STROKE_WIDTH,
          strokeLinejoin: 'round',
        };

  const shape = <Shape key={index} {...shapeProps} {...paintProps} />;

  if (part.transform) {
    return (
      <g key={index} transform={part.transform}>
        {shape}
      </g>
    );
  }

  return shape;
};

// One self-contained badge SVG: white medallion, progress ring, facet glyph.
// Sized with width/height 1em like the icons it replaces, so existing
// font-size based layouts keep working.
//
// Ring semantics: earned tiered badges close the ring progressively across
// their family (`tier`, 0..1); locked badges show progress toward the goal
// (`progress`, 0..1); earned one-off milestones get a full ring. Tiered
// badges stay filled (solid gray facets) even when locked.
export const FacetBadge = ({
  glyph,
  earned = false,
  rare = false,
  tone,
  tiered = false,
  tier,
  progress,
  ...props
}) => {
  const parts = GLYPHS[glyph];

  if (!parts) {
    return null;
  }

  const categoryTone = rare ? FACET_TONES.rare : tone || FACET_TONES.sessions;
  const glyphTone = earned ? categoryTone : LOCKED_TONE;
  const filled = earned || tiered;

  let ringProgress = 0;
  if (earned) {
    ringProgress = typeof tier === 'number' ? Math.max(tier, MIN_TIER_RING) : 1;
  } else {
    ringProgress = clampProgress(progress);
  }

  return (
    <svg width="1em" height="1em" viewBox="0 0 110 110" aria-hidden="true" {...props}>
      <circle
        cx={55}
        cy={55}
        r={47}
        fill={MEDALLION_FILL}
        style={{ filter: 'drop-shadow(0 2px 6px rgba(33, 38, 45, 0.12))' }}
      />
      <circle cx={55} cy={55} r={51} fill="none" stroke={RING_TRACK} strokeWidth={4} />
      {ringProgress > 0 && (
        <circle
          className="facet-ring-fill"
          cx={55}
          cy={55}
          r={51}
          fill="none"
          stroke={categoryTone}
          strokeWidth={4}
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray={`${(ringProgress * 100).toFixed(1)} 100`}
          transform="rotate(-90 55 55)"
        />
      )}
      <g
        transform="translate(27 27) scale(0.56)"
        style={
          earned ? { filter: `drop-shadow(0 5px 9px ${hexToRgba(categoryTone, 0.4)})` } : undefined
        }
      >
        {parts.map((part, index) => renderGlyphPart(part, index, { filled, tone: glyphTone }))}
      </g>
    </svg>
  );
};

export const createFacetIcon = (glyph, options = {}) => ({
  earned: (props) => <FacetBadge glyph={glyph} earned {...options} {...props} />,
  normal: (props) => <FacetBadge glyph={glyph} {...options} {...props} />,
});
